/**
 * Dashboard Planner Grid — shared constants + helpers (Feb 2026)
 * ---------------------------------------------------------------
 * Single source of truth for the day-planner timeline math. Both the
 * legacy `components/dashboard/Timeline.jsx` and the new V3
 * `DailyDashboardPane[variant="timeline"]` read from here, so the two
 * renderers can never drift.
 *
 * Anything that lives here is verbatim from the original Timeline.jsx
 * contract (spec: `/app/memory/SPEC_DD2_PLANNER_V3.md`).
 */

export const START_HOUR = 4;    // rail starts at 04:00
export const END_HOUR = 24;     // rail ends at 24:00 (exclusive)
export const SLOT_MINUTES = 15;
export const SLOT_HEIGHT = 30;  // px per 15-min slot
export const PX_PER_HOUR = SLOT_HEIGHT * (60 / SLOT_MINUTES); // 120 px/h
export const GRIP_WIDTH = 24;   // horizontal offset per overlap stack level

/** Canonical effort → minutes map (mirrors EFFORT_OPTIONS in TaskCard.jsx). */
export const EFFORT_MINUTES = {
  1: 15,
  2: 30,
  3: 60,
  5: 180,
  8: 360,
  13: 600,
};

/** Build the full flat list of 15-min slots between [START_HOUR, END_HOUR). */
export function generateTimeSlots() {
  const slots = [];
  for (let hour = START_HOUR; hour < END_HOUR; hour++) {
    for (let minute = 0; minute < 60; minute += SLOT_MINUTES) {
      slots.push({
        hour,
        minute,
        time: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
      });
    }
  }
  return slots;
}

export const TIME_SLOTS = generateTimeSlots();

/** Parse "HH:mm" → { hour, minute } (returns null on falsy input). */
export function parseHHmm(str) {
  if (!str || typeof str !== 'string') return null;
  const parts = str.split(':');
  if (parts.length < 2) return null;
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return { hour: h, minute: m };
}

/** "HH:mm" → decimal hour (e.g. "10:45" → 10.75). */
export function timeToHours(str) {
  const p = parseHHmm(str);
  if (!p) return null;
  return p.hour + p.minute / 60;
}

/** Duration in *hours* for a task — closed-view canonical sizing.
 *
 *    Priority order (re-ranked Feb 2026 so E-tier closed cards always
 *    occupy their canonical effort-based slot regardless of start/end):
 *
 *      1. task_estimated_minutes (explicit per-task override)
 *      2. EFFORT_MINUTES[task_estimated_effort]   ← E5 always = 180 min
 *      3. start & end time range (used only if neither effort nor minutes set)
 *      4. 0.5 h (= 2 slots) fallback.
 *
 *  Rationale: the day-planner displays the closed card at its canonical
 *  effort size. If a user wants a different visual length they update the
 *  effort score (or task_estimated_minutes) — start/end no longer override.
 */
export function durationHoursFromTask(task) {
  if (!task) return 0.5;
  const mins = Number(task.task_estimated_minutes ?? task.estimated_minutes);
  if (!Number.isNaN(mins) && mins > 0) return mins / 60;
  const effort = task.task_estimated_effort;
  if (effort != null && EFFORT_MINUTES[effort] != null) {
    return EFFORT_MINUTES[effort] / 60;
  }
  const start = task.task_start_time || task.task_local_start_time || task.start_time;
  const end = task.task_end_time || task.task_local_end_time || task.end_time;
  const s = timeToHours(start);
  const e = timeToHours(end);
  if (s != null && e != null && e > s) return e - s;
  return 0.5;
}

/** Same as above, but returns integer *slots* (ceil) — used by ghost/stacking. */
export function durationSlotsFromTask(task) {
  const h = durationHoursFromTask(task);
  return Math.max(1, Math.ceil((h * 60) / SLOT_MINUTES));
}

/** Compute an item's slot position on the rail (null if it has no start). */
export function taskSlotPosition(task) {
  const start = task.task_start_time || task.task_local_start_time || task.start_time;
  const p = parseHHmm(start);
  if (!p) return null;
  const slotIndex = (p.hour - START_HOUR) * 4 + Math.floor(p.minute / SLOT_MINUTES);
  if (slotIndex < 0) return null;
  const durationSlots = durationSlotsFromTask(task);
  return {
    slotIndex,
    top: slotIndex * SLOT_HEIGHT,
    height: Math.max(durationSlots * SLOT_HEIGHT, SLOT_HEIGHT),
    durationSlots,
  };
}

/**
 * Overlap stacking (verbatim from Timeline.jsx §147-194). Returns
 * `{ task, pos, stackLevel, indent, zIndex }[]`, sorted by top-asc,
 * duration-desc. Smaller cards float on top.
 */
export function computeStacking(tasks) {
  const tasksWithPos = (tasks || [])
    .map((t) => ({ task: t, pos: taskSlotPosition(t) }))
    .filter(({ pos }) => pos !== null);

  tasksWithPos.sort((a, b) => {
    if (a.pos.top !== b.pos.top) return a.pos.top - b.pos.top;
    return b.pos.durationSlots - a.pos.durationSlots;
  });

  const result = [];
  for (let i = 0; i < tasksWithPos.length; i++) {
    const current = tasksWithPos[i];
    const currentEnd = current.pos.top + current.pos.height;
    let stackLevel = 0;
    for (let j = 0; j < i; j++) {
      const other = tasksWithPos[j];
      const otherEnd = other.pos.top + other.pos.height;
      if (current.pos.top < otherEnd && currentEnd > other.pos.top) {
        if (current.pos.durationSlots <= other.pos.durationSlots) {
          stackLevel = Math.max(stackLevel, (result[j]?.stackLevel || 0) + 1);
        }
      }
    }
    result.push({
      ...current,
      stackLevel,
      indent: stackLevel * GRIP_WIDTH,
      zIndex: 10 + stackLevel,
    });
  }
  return result;
}

/** Fibonacci-bucket duration (mins) → canonical effort score.
 *
 *   Boundary policy (Feb 2026 — re-snapped so a 45-min task does NOT round
 *   up to E3): only durations of EXACTLY 60 min and above promote to E3.
 *   31-59 min stays at E2.
 */
export function minutesToEffort(durationMins) {
  if (durationMins <= 15) return 1;
  if (durationMins < 60) return 2;
  if (durationMins <= 60) return 3;
  if (durationMins <= 180) return 5;
  if (durationMins <= 360) return 8;
  return 13;
}

/** Perimeter color per hour (left rail dot / label) — matches Timeline.jsx mood. */
export function plannerHourColor(hour) {
  if (hour < 12) return '#16a34a';
  if (hour < 15) return '#2563eb';
  if (hour < 18) return '#d97706';
  return '#dc2626';
}

/** End-time for a drag-create: start of the slot AFTER the last selected slot. */
export function dragCreateEndTime(maxSlotIndex) {
  const endSlotIdx = Math.min(maxSlotIndex + 1, TIME_SLOTS.length - 1);
  return TIME_SLOTS[endSlotIdx]?.time || TIME_SLOTS[TIME_SLOTS.length - 1].time;
}
